import { useCallback, useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useExpandedColumnMirror } from "../lib/useExpandedColumnMirror.js";
import { useDebouncedValue } from "../lib/useDebouncedValue.js";
import { pipelineFilterText, andQueries } from "../lib/queryPipeline.js";
import { suggestValues as suggestQueryValues } from "../lib/queryLangSuggest.js";
import { formatCompact } from "../lib/renderers.js";
import { themePalette } from "../lib/shell.js";
import {
  DIMENSIONS,
  encodeScopePath,
  scopeStepLabel,
  useLabels,
  isDimensionVisible,
} from "../lib/dimensionDefs.js";
import { comparisonsEnabled } from "../lib/featureFlags.js";
import { contextModeAvailable, chatModeAvailable, rightPanelModes, panelModeDeniedFallback } from "../lib/rightPanelMode.js";
import { takeDetailExpandRequest } from "../lib/enterDetailFullScreen.js";
import { useQuery, query } from "../lib/dataClient.js";
import { computeAttributions, CONTEXT_WINDOW_DEFAULT_MS, CONTEXT_DIRECTION_DEFAULT } from "../lib/attribution.js";
import ScopedView, { CellCoOccurrencePanel } from "../components/ScopedView.jsx";
import EndpointScopeBoundary from "../components/EndpointScopeBoundary.jsx";
import { COMMAND_EVENT_TYPES_ALL } from "../components/CommandLogPanel.jsx";
import ContextPanel, { ScopeChip, traceHeaderByline } from "../components/ContextPanel.jsx";
import ScopeEscalationControl from "../components/escalation/ScopeEscalationControl.jsx";
import SignalDefinitionPanel from "../components/lenses/SignalDefinitionPanel.jsx";
import SignalsCatalogPanel from "../components/lenses/SignalsCatalogPanel.jsx";
import MemoryScopePanel from "../components/lenses/MemoryScopePanel.jsx";
import { isMemoryProjectId } from "../lib/memoryNames.js";
import { findTriggerByCommand } from "../lib/triggers/triggerStore.js";
import { TELEMETRY_PAGE_LIMIT } from "../lib/typedTelemetry.js";
import TabStrip from "../components/TabStrip.jsx";
import ModeRail, { MODE_ICONS } from "../components/ModeRail.jsx";
import { subscribeDock, getDockChatBar } from "../lib/dockStore.js";
import { useDockAvailabilityClaim, useDockColumnExpandedPublisher, useDockHostPageClaim } from "../lib/dockClaims.js";
import { readUrlParam, writeUrlParam } from "../lib/urlParams.js";
import ScrollArea from "../components/ScrollArea.jsx";
import RedactedByPolicy from "../components/RedactedByPolicy.jsx";
import UnavailableLinkPage from "../components/UnavailableLinkPage.jsx";
import KeptCopyNotice from "../components/KeptCopyNotice.jsx";
import { hasPermission, hasSensitivePermission, useMyPermissions } from "../lib/permissions.js";
import { analyticsErrorCopy } from "../lib/analyticsClient.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { EYEBROW_CLASS, NOTE_CLASS } from "../lib/ui.js";
import { timeLabelFormatter, dayLabelFormatter, parseServerTimestamp } from "../lib/traceUtils.js";
import { makeTraceTab, makeLoadingTrace } from "../lib/traceTabs.js";
import { createSessionTabReader } from "../lib/sessionTabReader.js";
import { focalRequestIdOf, parseTraceAddress, traceAddressOf, traceAddressOfRow } from "../lib/traceAddress.js";
import { resolveTrace } from "../lib/traceRecordHydration.js";
import { phAttr } from "../lib/analytics/attrs.js";
import { ACTION, ATTRIBUTE } from "../lib/analytics/surfaceVocabulary.js";
import { ITEM_UNAVAILABLE_MESSAGE, openCurrentOrganizationHome } from "../lib/tenantLink.js";

// Trailing-edge debounce for the search inputs that cross the network (trace
// query + command search). Held back this long after the last keystroke before
// a request fires, so a burst of typing collapses to one planner round-trip.
const SEARCH_DEBOUNCE_MS = 500;

function formatCellTabLabel(data) {
  const name = data?.cell?.cluster?.name || "Cell";
  const start = data?.cell?.bucketStart;
  if (!start) return name;
  const d = new Date(start);
  return `${name} · ${d.getMonth() + 1}/${d.getDate()}`;
}

// Minimal seed for a command tab opened before its row is in scope
// (deep link / reload). getEvent hydrates the real fields by id.
function makeLoadingCommand(commandId) {
  return {
    id: commandId,
    timestamp: Date.now(),
    command: "Loading…",
    tool: "",
    exitCode: 0,
    durationMs: 0,
    endpointId: "",
    endpointName: "",
    sessionId: "",
    sessionTitle: "",
    model: "",
    stdout: "",
    eventType: "",
    eventData: null,
  };
}

export function CommandDetail({ command, loading = false, error = null, onScopeNavigate }) {
  const ok = command.exitCode === 0;
  const data = command.eventData || null;
  // Exit code / duration / stdout are placeholders for every event type
  // except a process launch (the schema carries no per-event exec
  // signal). Surface them only for ProcessStart so config / file / DNS /
  // network rows don't show a misleading "exit 0 · 0ms · empty output".
  const showExec = command.eventType === "ProcessStart";
  // Event details can carry sensitive content such as command lines.
  // Gate the detail fields on the sensitive-read
  // permission; show a "redacted by policy" marker in its place rather
  // than omitting the section. Redact while the permission set is still
  // loading (fail closed) so payloads never flash before we know.
  const caps = useMyPermissions();
  const canSeeRawPayload = hasSensitivePermission(caps, PERMISSIONS.EVENTS_READ_SENSITIVE);
  // Resolve the endpoint hostname from the dashboard's lazy label cache
  // (same source the sidebar / trace log use). useLabels primes + fetches
  // and re-renders when the name lands; scopeStepLabel reads it back, and
  // we fall back to the short id while it's still resolving.
  const endpointSteps = command.endpointId ? [{ dim: "endpoints", id: command.endpointId }] : [];
  useLabels(endpointSteps);
  const endpointLabel = (() => {
    if (!command.endpointId) return "";
    const resolved = scopeStepLabel({ dim: "endpoints", id: command.endpointId });
    return resolved && resolved !== command.endpointId ? resolved : command.endpointId.slice(0, 8);
  })();
  if (error && !command.eventType) {
    return (
      <ScrollArea aria-busy={false} className="min-h-0 h-full" contentClassName="py-[12px]">
        <div role="alert">{error}</div>
      </ScrollArea>
    );
  }
  return (
    <ScrollArea aria-busy={loading} className="min-h-0 h-full" contentClassName="grid gap-[12px] py-[12px] pr-[2px]">
      {error && <div role="alert">{error}</div>}
      <div className="grid gap-[4px]">
        <span className={EYEBROW_CLASS}>Command</span>
        <pre className="m-0 whitespace-pre-wrap break-all text-[13px] leading-[1.4] font-medium text-[color:var(--ink)]">$ {command.command}</pre>
      </div>

      {showExec && (
        <div className="grid gap-[4px]">
          <span className={EYEBROW_CLASS}>Output</span>
          <pre className={["m-0 whitespace-pre-wrap break-all text-[12px] leading-[1.4] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[8px]", ok ? "text-[color:var(--ink)]" : "text-red-400"].join(" ")}>{command.stdout || "(no output captured)"}</pre>
        </div>
      )}

      {!data && loading && (
        <div className="text-[12px] leading-[1.3] text-[color:var(--muted-soft)]" aria-live="polite">Loading event details...</div>
      )}

      {data && (
        <details className="border-t border-[color:var(--line-soft)] pt-[12px]">
          <summary className="cursor-pointer text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-[color:var(--ink)]">Event details</summary>
          {canSeeRawPayload ? (
            <pre className="m-0 mt-[6px] max-h-[320px] overflow-auto whitespace-pre-wrap break-all rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[8px] text-[11px] leading-[1.4] font-mono text-[color:var(--muted)]">{JSON.stringify(data, null, 2)}</pre>
          ) : (
            <div className="mt-[6px]"><RedactedByPolicy what="The event details" /></div>
          )}
        </details>
      )}

      <div className="grid gap-[8px] border-t border-[color:var(--line-soft)] pt-[12px]">
        {showExec && (
          <div className="grid grid-cols-2 gap-[8px]">
            <div className="grid gap-[2px]">
              <span className={NOTE_CLASS}>Exit code</span>
              <strong className={["text-[13px] leading-[1.08] font-medium", ok ? "text-[color:var(--ink)]" : "text-red-400"].join(" ")}>{command.exitCode}</strong>
            </div>
            <div className="grid gap-[2px]">
              <span className={NOTE_CLASS}>Duration</span>
              <strong className="text-[13px] leading-[1.08] font-medium text-[color:var(--ink)]">{command.durationMs}ms</strong>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-[8px]">
          <div className="grid gap-[2px]">
            <span className={NOTE_CLASS}>Tool</span>
            <strong className="text-[13px] leading-[1.08] font-medium text-[color:var(--ink)]">{command.tool}</strong>
          </div>
          {command.model && (
            <div className="grid gap-[2px]">
              <span className={NOTE_CLASS}>Model</span>
              <strong className="text-[13px] leading-[1.08] font-medium text-[color:var(--ink)]">{command.model}</strong>
            </div>
          )}
        </div>
        {/* Endpoint + session are real scope dimensions — render them as
            chips that append a step to the current scope (drill in), like
            the trace detail. Tool/Timestamp stay plain: the synthesized
            tool value isn't a tools-dimension entity (it'd dead-end). */}
        {(command.endpointId || command.sessionId) && (
          <div className="grid gap-[3px]">
            <span className={NOTE_CLASS}>Scope</span>
            <div className="flex flex-wrap gap-[4px]">
              {command.endpointId && (
                <ScopeChip
                  dim="endpoints"
                  label={endpointLabel}
                  title={`Add endpoint ${command.endpointId} to scope`}
                  onClick={onScopeNavigate ? () => onScopeNavigate("endpoints", command.endpointId) : undefined}
                />
              )}
              {command.sessionId && (
                <ScopeChip
                  dim="sessions"
                  label={command.sessionTitle || command.sessionId.slice(0, 8)}
                  title={`Add session ${command.sessionId} to scope`}
                  onClick={onScopeNavigate ? () => onScopeNavigate("sessions", command.sessionId) : undefined}
                />
              )}
            </div>
          </div>
        )}
        <div className="grid gap-[2px]">
          <span className={NOTE_CLASS}>Timestamp</span>
          <strong className="text-[13px] leading-[1.08] font-medium text-[color:var(--ink)]">{dayLabelFormatter.format(command.timestamp)} {timeLabelFormatter.format(command.timestamp)}</strong>
        </div>
      </div>
    </ScrollArea>
  );
}

/**
 * Breadcrumb that progressively drops dimension prefixes (shallowest first)
 * when the container overflows, keeping deeper nodes more legible.
 *
 * Click on an intermediate segment opens a popover so the user picks
 * between "Jump to this scope" (truncate; deeper filters drop) and
 * "Delete this filter" (surgical removal of just this step). Auto-
 * deleting on plain click was the old behavior — easy to do by accident
 * and visually indistinguishable from a navigation tap.
 */
function ScopeBreadcrumb({ scopePath, onChange }) {
  const ref = useRef(null);
  // `null` = closed; otherwise the index of the segment whose popover
  // is open. Each popover is anchored against its own button rect.
  const [openIndex, setOpenIndex] = useState(null);
  const segmentRefs = useRef(new Map());
  const popoverRef = useRef(null);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    function fit() {
      const prefixes = el.querySelectorAll("[data-crumb-prefix]");
      for (const p of prefixes) p.style.display = "";
      // Drop prefixes from shallowest (lowest index) until it fits
      for (const p of prefixes) {
        if (el.scrollWidth <= el.clientWidth) break;
        p.style.display = "none";
      }
    }

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [scopePath]);

  // Position the popover under the active segment.
  useLayoutEffect(() => {
    if (openIndex === null) return;
    const place = () => {
      const node = segmentRefs.current.get(openIndex);
      if (!node) return;
      const r = node.getBoundingClientRect();
      setPopoverPos({ top: r.bottom + 4, left: r.left });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [openIndex]);

  // Outside click / Escape closes.
  useEffect(() => {
    if (openIndex === null) return;
    const onDown = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      const segNode = segmentRefs.current.get(openIndex);
      if (segNode?.contains(e.target)) return;
      setOpenIndex(null);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpenIndex(null); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [openIndex]);

  const activeStep = openIndex !== null ? scopePath[openIndex] : null;
  const activeIsLast = openIndex === scopePath.length - 1;

  return (
    <>
      <span ref={ref} className="flex items-baseline min-w-0 overflow-hidden tracking-[0.02em]">
        <button
          type="button"
          className="shrink-0 cursor-pointer whitespace-nowrap hover:text-[color:var(--ink)] transition-colors"
          onClick={() => onChange([])}
        >
          Overview
        </button>
        {scopePath.map((step, i) => {
          const dim = DIMENSIONS[step.dim];
          const prefix = dim?.label || step.dim;
          const value = scopeStepLabel(step);
          const isLast = i === scopePath.length - 1;
          const stepKey = Array.isArray(step.ids) && step.ids.length
            ? step.ids.join("+")
            : (step.id ?? "");
          return (
            <span key={`${step.dim}:${stepKey}`} className={isLast ? "min-w-0 truncate" : "shrink-0"}>
              <span className="px-[5px] text-[color:var(--muted-soft)]" aria-hidden="true">›</span>
              <button
                ref={(node) => {
                  if (node) segmentRefs.current.set(i, node);
                  else segmentRefs.current.delete(i);
                }}
                type="button"
                title={`${prefix}: ${value}`}
                aria-haspopup="menu"
                aria-expanded={openIndex === i}
                className="cursor-pointer whitespace-nowrap transition-colors hover:text-[color:var(--ink)]"
                onClick={() => setOpenIndex((cur) => (cur === i ? null : i))}
              >
                <span data-crumb-prefix={i}>{prefix}:&nbsp;</span>
                <span className={isLast ? "normal-case tracking-normal" : undefined}>{value}</span>
              </button>
            </span>
          );
        })}
      </span>
      {openIndex !== null && activeStep ? (
        <div
          ref={popoverRef}
          role="menu"
          // tracking-normal forces a baseline so the breadcrumb's
          // tracking-[0.02em] doesn't bleed into the popover via the
          // surrounding flow.
          className="fixed z-[9999] grid min-w-[240px] max-w-[340px] gap-[1px] border border-[color:var(--line)] bg-[color:var(--panel)] p-[2px] tracking-normal shadow-lg"
          style={{ top: `${popoverPos.top}px`, left: `${popoverPos.left}px` }}
        >
          <button
            type="button"
            role="menuitem"
            disabled={activeIsLast}
            onClick={() => {
              setOpenIndex(null);
              onChange(scopePath.slice(0, openIndex + 1));
            }}
            className={[
              "grid gap-[2px] px-[10px] py-[7px] text-left transition-colors",
              activeIsLast
                ? "cursor-not-allowed opacity-40"
                : "cursor-pointer hover:bg-[color:var(--control-bg)]",
            ].join(" ")}
          >
            <span className="text-[12px] leading-[1.2] text-[color:var(--ink)]">Jump to this scope</span>
            <span className="text-[11px] leading-[1.3] text-[color:var(--muted)]">
              {activeIsLast
                ? "Already viewing this scope"
                : "Trim the scope to end here (deeper filters drop)"}
            </span>
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpenIndex(null);
              onChange(scopePath.filter((_, idx) => idx !== openIndex));
            }}
            className="grid cursor-pointer gap-[2px] px-[10px] py-[7px] text-left transition-colors hover:bg-[color:var(--control-bg)]"
          >
            <span className="text-[12px] leading-[1.2] text-[color:var(--ink)]">Delete this filter</span>
            <span className="text-[11px] leading-[1.3] text-[color:var(--muted)]">
              Remove just this step, keep the rest
            </span>
          </button>
        </div>
      ) : null}
    </>
  );
}

export default function ScopedPage(props) {
  return (
    <EndpointScopeBoundary scopePath={props.scopePath} focusHeader={props.focusHeader}
      onBack={() => { props.onEndpointTabChange?.("attribution"); props.onScopePathChange?.([]); }}>
      <ResolvedScopedPage {...props} />
    </EndpointScopeBoundary>
  );
}

function ResolvedScopedPage({
  scopePath,
  range,
  activityLens,
  focusDim = null,
  view = null,
  // Active influence-trace tab (parsed: { traceId, turnIndex, payload })
  // or null. When set, the main column shows the influence-trace surface
  // for that turn instead of the scope lens host.
  influenceTab = null,
  onOpenInfluenceTab,
  onOpenScopeTab,
  endpointTab,
  logTab,
  theme,
  focusHeader,
  onRangeChange,
  onActivityLensChange,
  onEndpointTabChange,
  onLogTabChange,
  onScopePathChange,
  // True only when Explore is the visible surface. ScopedPage stays
  // MOUNTED (just hidden + inert) under the Studio/Settings overlay, so
  // the dock is gated on visibility, not mount — otherwise ⌘K would dock
  // into the hidden Explore layer instead of opening the overlay there.
  dockVisible = true,
  // Overview pivot: when set, only the activity surface re-scopes here and
  // goes full-screen; the rest of the page stays on scopePath.
  surfaceScopeOverride = null,
  // The sidebar scope steer (already debounced + pipeline-stripped upstream).
  // It refines every surface on this page on top of `scopePath`; the trace
  // window's own query stacks beneath it (andQueries below).
  steerQuery = "",
  // Writes the CURRENT scope level's steer (App's prefix-keyed map). Lens
  // bodies use it to steer the scope from data (e.g. the Topic patterns
  // "steer to these sessions" action). Null when steering is gated off.
  onSteerScope = null,
}) {
  const palette = themePalette(theme);

  // Right-column panel mode. "context" → the rail shows the contrast /
  // trace tabs and the body shows context; "chat" → the rail shows the
  // docked chat's session tabs and the body shows the chat. The mode
  // switch is pinned to the rail's leading edge (left of the first tab).
  // chatBar is the chat's published tab model (tabs + new/PDF actions),
  // surfaced via dockStore so the rail can render it without lifting
  // ChatConsole's state up here.
  // Right-column mode (context ⇄ chat). Chat is the default; an
  // explicit switch to context is persisted in the hash as
  // `?panel=context` so a reload keeps the choice instead of silently
  // snapping back to chat (the chat session itself already survives in
  // localStorage; only this view bit was being lost). Old `?panel=chat`
  // URLs still resolve to chat.
  const [panelMode, setPanelMode] = useState(() => (readUrlParam("panel") === "context" ? "context" : "chat"));
  useEffect(() => {
    writeUrlParam("panel", panelMode === "context" ? "context" : null);
  }, [panelMode]);
  // Live collapse state of the right detail panel, reported up by SplitPage.
  const [detailCollapsed, setDetailCollapsed] = useState(false);
  // Tell the (app-level) ChatConsole that the dock lives on this page AND is
  // visible AND the panel is open — so ⌘K surfaces the docked chat in the
  // column. When the panel is collapsed (or Explore is covered by the
  // Studio/Settings overlay), the dock is unavailable and ⌘K opens the
  // floating chat overlay instead.
  // Guarded on visibility: dockStore is a single global slot with
  // last-writer-wins, and in dashboard mode this page stays MOUNTED
  // (hidden + inert) under the overlay while CanvasHomePage owns the
  // dock. A hidden page writing `false` here clobbered the home page's
  // claim — with `?dcol=open` the collapse state settles after mount,
  // this effect re-ran, and the chat sidebar went permanently empty.
  // The hidden-page rule lives (and is tested) in dockClaims.js.
  useDockAvailabilityClaim(dockVisible, !detailCollapsed);
  useDockHostPageClaim(dockVisible);
  // ⌘K (and any "open chat" intent) flips the right column to Chat and
  // un-collapses it if it was hidden off-screen. The keystroke lives in
  // ChatConsole, which dispatches this once it sees the dock is available.
  // Always dispatch the reveal — SplitPage no-ops it when the column is
  // already open, and decides off its LIVE collapse ref (the `?dcol` hash
  // lags the animation, so we can't gate on it here without missing a
  // ⌘K fired mid-collapse). The suppress flag keeps the canvas auto-expand
  // (below) from firing a SECOND, competing FLIP on the same column while
  // the reveal animates — which is what left the focus column stuck
  // mid-resize.
  const suppressAutoExpandRef = useRef(false);
  useEffect(() => {
    const onActivate = () => {
      suppressAutoExpandRef.current = true;
      setPanelMode("chat");
      window.dispatchEvent(new CustomEvent("origin-detail-uncollapse"));
      setTimeout(() => { suppressAutoExpandRef.current = false; }, 450);
    };
    window.addEventListener("origin-chat-activate", onActivate);
    return () => window.removeEventListener("origin-chat-activate", onActivate);
  }, []);
  // The chat handing a trace over (App's trace navigation) brings the context
  // column forward: a trace tab that is already open has no hydration to do
  // it, and would otherwise stay behind the chat. The tab it names arrives in
  // a transition, a render or more after this switch, so until the page is on
  // it the context is pending rather than emptied (handoverPending below).
  const [handoverTab, setHandoverTab] = useState(null);
  useEffect(() => { setHandoverTab(null); }, [endpointTab]);
  useEffect(() => {
    const onActivate = (e) => {
      setHandoverTab(e?.detail?.tab || null);
      setPanelMode("context");
      window.dispatchEvent(new CustomEvent("origin-detail-uncollapse"));
    };
    window.addEventListener("origin-context-activate", onActivate);
    return () => window.removeEventListener("origin-context-activate", onActivate);
  }, []);
  const chatBar = useSyncExternalStore(subscribeDock, getDockChatBar, () => null);
  // Number of canvas items (changes on every add/remove) — drives the
  // force-expand below so each mutation pops the chat to two columns.
  const canvasCount = chatBar?.canvasCount ?? 0;

  // Chat-column expand: the docked chat can expand to fill the content
  // area, hiding the scope column — useful when the canvas needs room.
  // ContentShell owns the actual expand (we drive it via the
  // `origin-column-expand` event and mirror its `columnexpandchange`
  // signal here). We track the RAW id of whatever column is expanded so
  // two things derive from it: whether the chat (detail) column itself is
  // expanded (the 2-col toggle), and whether the detail panel is hidden
  // because ANOTHER column expanded over it (e.g. the activity surface
  // taking over) — in which case the rail must not light a mode, since
  // the panel that mode refers to isn't on screen. "detail" is the right
  // column's id.
  const expandedColumnId = useExpandedColumnMirror();
  const chatColExpanded = expandedColumnId === "detail";
  const detailHiddenByExpand = expandedColumnId != null && expandedColumnId !== "detail";
  const expandChatColumn = (mode) =>
    window.dispatchEvent(new CustomEvent("origin-column-expand", { detail: { columnId: "detail", mode } }));
  // Publish the expanded state to the dock store so ChatConsole can gate
  // the canvas on it — the canvas only renders in two-column mode.
  // Same hidden-page rule as the availability claim above.
  useDockColumnExpandedPublisher(dockVisible, chatColExpanded);
  // Force-expand on every canvas mutation (canvasCount changes on each
  // add/remove), collapse when the canvas empties. Keying on the COUNT —
  // not a boolean — means a manual collapse in between is re-expanded by
  // the next mutation, which is the intent: the canvas lives in 2-col, so
  // mutating it pops the chat open. Chat mode only; never touches context.
  useEffect(() => {
    if (panelMode !== "chat") return;
    // Skip while a ⌘K reveal of a collapsed column is settling (see above)
    // so we don't fire a competing FLIP; the next canvas mutation re-runs
    // this and expands as usual.
    if (suppressAutoExpandRef.current) return;
    expandChatColumn(canvasCount > 0 ? "expand" : "collapse");
  }, [panelMode, canvasCount]);
  // Collapse the chat-driven expand only on the chat→context TRANSITION —
  // NOT whenever something is expanded in context mode (that would kill a
  // legitimately-expanded context panel the instant the user opened it). A
  // trace the chat opened full screen takes the switch to expand instead
  // (enterDetailFullScreen).
  const prevPanelModeRef = useRef("context");
  useEffect(() => {
    const prev = prevPanelModeRef.current;
    prevPanelModeRef.current = panelMode;
    if (prev === "chat" && panelMode !== "chat") expandChatColumn(takeDetailExpandRequest() ? "expand" : "collapse");
  }, [panelMode]);

  // Defer the scope path so the sidebar/tabs update immediately while the
  // expensive page content (trace filtering, contrast matrix) catches up.
  const deferredScopePath = useDeferredValue(scopePath);

  // ── Trace log search + sort. State lives here so the API call
  //    (getTraces) can push them down to ClickHouse — keystroke search
  //    on a 1000-row page misses the rest of the dataset; pushing
  //    search/sort to the server hits the full window.
  const [traceSort, setTraceSort] = useState("newest");
  // Query-language filter for the trace log (QueryBar) — the single trace
  // search now (it subsumes the old substring Search: bare words match prompt
  // text + provider). Persisted in the URL (`tq`) so a refined view is
  // shareable.
  //
  // The fetch query is DEBOUNCED, not merely deferred: every keystroke would
  // otherwise become a fresh analytics-planner request across getTraces +
  // getOverviewMetrics + the activity bundle + any scoped kernel — the
  // planner-hammering the user flagged. `traceQuery` stays live (it drives the
  // input, chips + diagnostics for instant feedback); `deferredTraceQuery`
  // only catches up SEARCH_DEBOUNCE_MS after the last keystroke, and
  // `traceQueryPending` lets the surfaces show a "searching" affordance the
  // moment a key is pressed.
  const [traceQuery, setTraceQuery] = useState(() => readUrlParam("tq") || "");
  const [deferredTraceQuery, traceQueryPending] = useDebouncedValue(traceQuery, SEARCH_DEBOUNCE_MS);
  const onTraceQueryChange = (q) => { setTraceQuery(q); writeUrlParam("tq", q || null); };
  // A pipeline query (`… |> aggregate |> render`) drives an inline chart, not the
  // trace list. Strip to the filter stage for the row/metric analytics so they
  // never compile the aggregate/render words as filter terms (the chart itself is
  // compiled in ScopeOverviewBody and rendered via ActivityChart's KernelWidget).
  const traceFilterQuery = useMemo(() => pipelineFilterText(deferredTraceQuery), [deferredTraceQuery]);
  // Layer the sidebar scope steer ON TOP of the trace-window query: every data
  // surface on this page reads scope ∩ steer ∩ trace-bar. (The sidebar facet
  // counts get the steer alone — they never saw the trace-window query.)
  const steeredQuery = useMemo(() => andQueries(steerQuery, traceFilterQuery), [steerQuery, traceFilterQuery]);
  // QueryBar value autocomplete: bound to the CURRENT scope + range so
  // suggestions reflect the view being refined (scope-aware). The suggester is
  // analytics-only; unsupported suggestions return no values.
  const querySuggestValues = useCallback(
    (fieldKey, term) => suggestQueryValues(fieldKey, term, { scopePath, range }),
    [scopePath, range],
  );
  const [commandSort, setCommandSort] = useState("newest");
  const [commandSearch, setCommandSearch] = useState("");
  const [commandEventTypes, setCommandEventTypes] = useState(
    () => [...COMMAND_EVENT_TYPES_ALL],
  );
  // Debounce command search before querying typed telemetry fields.
  const [deferredCommandSearch, commandSearchPending] = useDebouncedValue(commandSearch, SEARCH_DEBOUNCE_MS);
  // Context mode (on the command log): mirrors the trace listing's
  // "Context" filter. When ON + a trace is selected, the command log
  // shows commands within a user-configurable temporal window of the
  // selected trace, merged with commands probabilistically attributed
  // to it. Each row picks up annotations referencing other traces it
  // also matches. Default ON so opening a trace narrows automatically.
  const [commandContextOn, setCommandContextOn] = useState(true);
  const [commandContextWindowMs, setCommandContextWindowMs] = useState(CONTEXT_WINDOW_DEFAULT_MS);
  const [commandContextDirection, setCommandContextDirection] = useState(CONTEXT_DIRECTION_DEFAULT);
  const [commandContextFocalOnly, setCommandContextFocalOnly] = useState(false);
  // Tab history is hoisted above the data queries because downstream
  // logic reads off the hydrated trace tab.
  const [openTabs, setOpenTabs] = useState([]);
  // Reveals a closed detail column. Only an open the user asked for calls it
  // (the select/open/pin handlers below), so individual lenses don't each have
  // to call slot.onExpandDetail. A tab restored from the URL never does: this
  // page remounts when the scope moves onto or between endpoints
  // (EndpointScopeBoundary) and re-adds the focal trace tab, and a column the
  // user closed stays closed. SplitPage no-ops the reveal when already open.
  const revealDetail = () => window.dispatchEvent(new CustomEvent("origin-detail-uncollapse"));
  // Influence tracing — each trace is a real Explore tab (owned by App's
  // explorer strip via onOpenInfluenceTab). When the active explorer tab
  // is an influence tab, App passes `influenceTab` here and the main
  // column swaps to the influence surface; the trace it's about stays in
  // the right column, and cited traces open back into it via
  // handleSelectTrace. We resolve the tab's { traceId, turnIndex } to a
  // live { trace, turn } by fetching the trace (works on reload too).
  const [influenceDescriptor, setInfluenceDescriptor] = useState(null);
  useEffect(() => {
    if (!influenceTab?.traceId) { setInfluenceDescriptor(null); return undefined; }
    let on = true;
    // Provisional (loading) descriptor right away so the center shows the
    // influence surface, not a flash of the scope overview, while we fetch.
    setInfluenceDescriptor({ key: influenceTab.payload, trace: null, turn: null, turnIndex: influenceTab.turnIndex, loading: true });
    // The turn index was armed against the trace as the panel shows it, which
    // is the conversation-record axis once its first page has landed.
    // resolveTrace takes the open tab, or fetches the trace and its newest
    // page the way the tab reader would (traceRecordHydration.js), so the
    // index lands on the same turn here.
    resolveTrace(influenceTab.traceId, { tabs: openTabs, query }).then((full) => {
      if (!on) return;
      if (!full) { setInfluenceDescriptor({ key: influenceTab.payload, trace: null, turnIndex: influenceTab.turnIndex, error: ITEM_UNAVAILABLE_MESSAGE }); return; }
      const turn = full.turns?.[influenceTab.turnIndex] || null;
      setInfluenceDescriptor({ key: influenceTab.payload, trace: full, turn, turnIndex: influenceTab.turnIndex });
    }).catch((e) => {
      // Without this, a network/auth/missing-trace rejection leaves the
      // provisional loading descriptor in place and the surface is stuck
      // on "Loading trace…" with no way out. Surface the error instead.
      if (on) setInfluenceDescriptor({ key: influenceTab.payload, trace: null, turnIndex: influenceTab.turnIndex, error: analyticsErrorCopy(e, caps).message });
    });
    return () => { on = false; };
  }, [influenceTab?.payload]); // eslint-disable-line react-hooks/exhaustive-deps
  // The currently-active trace tab (if endpointTab is on a trace).
  const activeTraceTabId = endpointTab?.startsWith("trace:") ? endpointTab.slice("trace:".length) : "";
  // Persisted focal trace: when the user selects a command/cell, the
  // active trace tab changes but the command log should keep its
  // focal-trace context (the "since last request" anchor + window
  // bounds + attribution links). Track the most recently-active trace
  // tab id that's still open. Cleared when that trace tab is closed.
  const [lastFocalTraceId, setLastFocalTraceId] = useState("");
  useEffect(() => {
    if (activeTraceTabId) setLastFocalTraceId(activeTraceTabId);
  }, [activeTraceTabId]);
  // Resolve focal: prefer the active trace tab, fall back to the
  // last-active trace as long as that tab is still open.
  const selectedTraceTabId = activeTraceTabId
    || (lastFocalTraceId && openTabs.some((t) => t.type === "trace" && t.id === lastFocalTraceId)
      ? lastFocalTraceId
      : "");
  const hydratedTraceTab = openTabs.find((t) => t.type === "trace" && t.id === selectedTraceTabId && t.hydrated);
  const hydratedTrace = hydratedTraceTab?.data ?? null;
  // The focal REQUEST behind that tab, for everything keyed by request id
  // (the request log's rows, command attribution, prev/next). See
  // focalRequestIdOf: the tab's address names a moment, not a request.
  const selectedTraceRequestId = focalRequestIdOf(openTabs.find((t) => t.type === "trace" && t.id === selectedTraceTabId)?.data);

  // ── Page-size caps with "load more" semantics ──
  // The backend honors any LIMIT we send, so growing the cap on click
  // is enough — ClickHouse re-runs the same WHERE+ORDER and re-streams
  // the larger slice. Caps reset whenever scope/sort/search change so
  // the user doesn't pay for stale rows after pivoting.
  // Initial fetch size for the trace + execution log queries. Sized
  // for fast cold-load: a single /v1/analytics:execute payload at
  // 1000 rows can take 300ms+ just to JSON.parse, becoming the
  // dominant first-paint blocker. The visible trace log is virtualised
  // and the user can pull more rows via "Load more" — so the only
  // cost of a smaller default is one extra click for users who
  // immediately scroll to the bottom.
  const PAGE = 200;
  // Commands carry the full `event_data` JSON (bash output, file diffs,
  // network bodies) — far heavier per-row than traces — so the initial
  // fetch is smaller. The user can pull more rows via "Load more".
  const COMMAND_PAGE = 100;
  const [traceLimit, setTraceLimit] = useState(PAGE);
  const [commandLimit, setCommandLimit] = useState(COMMAND_PAGE);
  const traceQueryKey = `${encodeScopePath(deferredScopePath)}|${traceSort}|${deferredTraceQuery}|${steerQuery}|${range}`;
  // Directional window bounds for the command-log Context filter.
  // When a trace is focal AND context mode is on, narrow the server
  // fetch to a time window around the focal trace's timestamp — with a
  // 5-minute buffer beyond the user's selected magnitude so the
  // attribution model (which can reach up to ~5min post-prompt) has
  // candidates to score. Without these bounds the default "newest"
  // sort returns commands globally most-recent, which usually excludes
  // any pre-prompt commands for older focal traces — and "−" mode
  // then has no data to filter against. Direction "around" populates
  // both bounds; "before"/"after" each populate one.
  // Window bounds are derived before traceLookup is built, so read the
  // Focal trace timestamp comes from the hydrated trace tab's
  // `eventTime` (a CH-naive string). `parseServerTimestamp` reads it as
  // UTC; `toClickhouseTimestamp` re-formats UTC components so the value
  // we send the gateway matches the column's stored representation
  // (CH-naive strings the gateway interprets as UTC).
  const focalQueryT = hydratedTrace?.eventTime ? parseServerTimestamp(hydratedTrace.eventTime) : 0;
  // The command log's window is anchored on a focal REQUEST; a tab on the
  // whole conversation names none, so it anchors nothing, as a session tab
  // never did. The same focal request drives the context auto-enable and the
  // attribution wiring below.
  const useWindowBounds = commandContextOn && selectedTraceRequestId && focalQueryT > 0;
  const toClickhouseTimestamp = (ms) => {
    const d = new Date(ms);
    const p = (n, w = 2) => String(n).padStart(w, "0");
    return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}.${p(d.getUTCMilliseconds(), 3)}`;
  };
  let windowAfterT = "";
  let windowBeforeT = "";
  if (useWindowBounds) {
    const wm = commandContextWindowMs;
    if (commandContextDirection === "before") {
      windowAfterT = toClickhouseTimestamp(focalQueryT - wm);
      windowBeforeT = toClickhouseTimestamp(focalQueryT);
    } else if (commandContextDirection === "after") {
      windowAfterT = toClickhouseTimestamp(focalQueryT);
      windowBeforeT = toClickhouseTimestamp(focalQueryT + wm);
    } else {
      windowAfterT = toClickhouseTimestamp(focalQueryT - wm);
      windowBeforeT = toClickhouseTimestamp(focalQueryT + wm);
    }
  }
  const commandQueryKey = `${encodeScopePath(deferredScopePath)}|${commandSort}|${deferredCommandSearch}|${range}|${windowAfterT}|${windowBeforeT}|${commandEventTypes.join(",")}`;
  useEffect(() => { setTraceLimit(PAGE); }, [traceQueryKey]);
  useEffect(() => { setCommandLimit(COMMAND_PAGE); }, [commandQueryKey]);

  // Permission set for the active user — used to render role-aware copy on a
  // raw-analytics denial (aggregate-only roles get a 403 from getTraces) and to
  // gate the sensitive enriched-prompt column out of the getTraces projection
  // (below) for a role lacking ai_sessions.read.sensitive.
  const caps = useMyPermissions();
  // Query-build gate for the sensitive enriched-prompt COLUMN in getTraces
  // (distinct from `canTracePrompts`, which HIDES the Trace-log tab). getTraces
  // still fires for a role without the sensitive read — the cluster swimlane /
  // contrast matrix on the Overview derive from its rows — so without omitting
  // the sensitive column the whole query 403s and the Overview blanks. Use
  // hasPermission (fail-OPEN while caps load/unavailable/degraded) NOT
  // hasSensitivePermission: an admin mid-load must still request the full
  // projection; we only drop the column once caps are RESOLVED and the
  // ai_sessions.read.sensitive grant is confirmed absent.
  const canReadSensitivePrompts = hasPermission(caps, PERMISSIONS.AI_SESSIONS_READ_SENSITIVE);

  // ── Filtered traces + commands ──
  // getTraces selects enriched prompt/response text (ai_sessions
  // sensitive), so for a role without that read it omits the column
  // (canReadSensitivePrompts) and the rows come back with summaryUnavailable
  // — keeping the non-sensitive trace data (and the Overview surfaces derived
  // from it) instead of a query-wide 403. A genuine denial (e.g. backend
  // disagrees) still sets `error`, which renders role-aware copy in the trace
  // log instead of an empty "no requests match" state — or a raw `403 {...}`.
  const { data: traces = [], loading: tracesLoading, error: tracesError } = useQuery("getTraces", {
    scopePath: deferredScopePath,
    sort: traceSort,
    query: steeredQuery,
    range,
    limit: traceLimit,
    canReadSensitivePrompts,
  });
  const tracesDenial = tracesError ? analyticsErrorCopy(tracesError, caps) : null;
  // The Execution log (getCommands) selects full raw `event_data` payloads,
  // so it's a RAW-tier read — an aggregate-only role (SecOps) is denied.
  // Gate the tab, its auto-fetch, and the default selection on the raw
  // permission. hasPermission fails open while caps load, so a pre-RBAC
  // tenant keeps the Execution log exactly as before.
  // Base analytics-read gate. Used to hide the influence-trace affordance
  // (a multi-step LLM-proxy run) from a role that can't run analytics —
  // firing it would 403 every step. Fails open while caps load.
  const canAnalytics = hasPermission(caps, PERMISSIONS.ANALYTICS_EXECUTE);
  // Chat gate: the docked chat needs the LLM provider descriptor —
  // without it the console's creds fetch 403s. Drops the rail's Chat mode
  // and steers panelMode to context (below); App unmounts the ChatConsole
  // singleton on the same predicate. Rules live in rightPanelMode.js
  // alongside contextModeAvailable.
  const canChat = chatModeAvailable(caps);
  // Chat is the panelMode default (and where an emptied context falls back),
  // so a denied role must be steered off it once the permission set
  // resolves — an effect, not the initializer, because caps arrive async.
  useEffect(() => {
    const fallback = panelModeDeniedFallback(panelMode, canChat);
    if (fallback) setPanelMode(fallback);
  }, [canChat, panelMode]);
  // Both logs select Sensitive content, so each is gated on the matching
  // `read.sensitive` (not on execute.raw — that only authorizes running a
  // query, and SecOps/Viewer hold it). The Trace log selects enriched
  // prompt/response text (ai_sessions sensitive); the Execution log selects
  // raw event payloads — bash output, file diffs, network bodies (events
  // sensitive). Without the matching read.sensitive the query 403s and the
  // panel reads as an empty "no data" state indistinguishable from a genuinely
  // empty window, so we hide the tab/panel and skip its fetch instead.
  // hasSensitivePermission fails CLOSED on a missing perm but open for a
  // pre-RBAC tenant, so legacy embeds keep both logs.
  // An escalation carries the permission tenant-wide but the scope only over
  // its target, so the permission alone would render the panel on every other
  // scope with nothing readable in it. `contentWithheld` is the read itself
  // reporting that back: prompts exist here and none of their bodies came back.
  const canTracePrompts = hasSensitivePermission(caps, PERMISSIONS.AI_SESSIONS_READ_SENSITIVE);
  // Held tenant-wide by an escalation but scoped to its target, so the
  // permission says yes on scopes the grant never reached. Read off the request
  // list, which is unconditional: the session list is gated on the tab being
  // open, and both grains read the same prompts over the same scope anyway.
  const promptsWithheldHere = Boolean(traces.contentWithheld);
  const canRawCommands = hasSensitivePermission(caps, PERMISSIONS.EVENTS_READ_SENSITIVE);
  // Land on a tab the role can actually see: the user's choice when both are
  // visible, else whichever single log is permitted (moot when neither is —
  // the whole log section is hidden). Keeps us from auto-firing a query that
  // 403s and collapses a stale `?log=commands` deep-link for a trace-only role.
  const effectiveLogTab =
    canTracePrompts && canRawCommands ? logTab
      : canRawCommands ? "commands"
        : logTab === "requests" ? "requests" : "sessions";

  // ── Sessions: the trace log's default grain ──
  // One row per conversation over the same scope, search and query as the
  // request list (getSessionLog compiles the scope through the same helper as
  // getTraces). Fetched only while the Sessions grain is showing: the Overview's
  // other surfaces derive from the request rows, which keep loading regardless.
  const [sessionSort, setSessionSort] = useState("newest");
  const [sessionLimit, setSessionLimit] = useState(PAGE);
  const { data: sessions = [], loading: sessionsLoading, error: sessionsError } = useQuery("getSessionLog", {
    scopePath: deferredScopePath,
    sort: sessionSort,
    query: steeredQuery,
    range,
    limit: sessionLimit,
    canReadSensitivePrompts,
  }, { enabled: canTracePrompts && effectiveLogTab === "sessions" });
  const sessionsDenial = sessionsError ? analyticsErrorCopy(sessionsError, caps) : null;
  // Execution-log fetch is gated on the user actually viewing the
  // Commands tab AND holding the raw permission. The EVENT query selects
  // full `event_data` payloads (bash output, file diffs, network bodies,
  // …) for up to L=1000 rows, which routinely takes 5–10 s end-to-end —
  // historically refired on every scope change even when the trace log
  // was the visible panel. Now it only fires when the commands tab is
  // visible; switching tabs hits the 30s useQuery cache.
  const commandsEnabled = effectiveLogTab === "commands" && canRawCommands;
  const { data: commands = [], loading: commandsLoading, error: commandsError } = useQuery("getCommands", {
    scopePath: deferredScopePath,
    sort: commandSort,
    search: deferredCommandSearch,
    range,
    limit: commandLimit,
    windowAfterT,
    windowBeforeT,
    eventTypes: commandEventTypes,
  }, { enabled: commandsEnabled });
  // Role-aware copy for a commands denial — if a getCommands somehow lands
  // a 403 (e.g. caps resolved raw-capable but the backend disagrees),
  // render this instead of the misleading "No commands match — widen the
  // date range" empty state.
  const commandsDenial = commandsError ? analyticsErrorCopy(commandsError, caps) : null;

  // ── Trace + command selection ──

  const traceLookup = useMemo(() => new Map(traces.map((t) => [t.id, t])), [traces]);
  const commandLookup = useMemo(() => new Map(commands.map((c) => [c.id, c])), [commands]);

  // Attribution: probabilistic links between commands and traces.
  // Recomputed when either side changes — both arrays are server-bounded
  // by `limit` (default 1000) and the scoring loop binary-searches the
  // candidate window per command, so this stays cheap.
  const attribution = useMemo(
    () => computeAttributions(traces, commands),
    [traces, commands],
  );

  // Deep-link hydration. If endpointTab arrives from the URL as
  // `trace:<uuid>` and we haven't opened that tab yet, fetch and add
  // it. The user-driven path (handleSelectTrace) opens the tab BEFORE
  // setting endpointTab, so the early-return makes this a no-op for
  // every interaction except the initial deep link.
  // (Cells / commands aren't hydrated this way — their full payload
  // depends on bucket / scope context that the URL doesn't carry.)
  const traceHydrationsRef = useRef(new Set());
  const hydrationAttemptRef = useRef(0);
  const commandHydrationsRef = useRef(new Set());
  const openTabsRef = useRef(openTabs);
  openTabsRef.current = openTabs;
  // Lands getTrace's answer on the tab opened under `address`. The answer
  // knows its own address (traceAddress.js): a tab opened on a bare request
  // id -- a thread hop, an older link -- is re-keyed to `session@request`,
  // and the URL follows when that tab is the active one.
  const endpointTabRef = useRef(endpointTab);
  endpointTabRef.current = endpointTab;
  const landTrace = useCallback((address, full, originScopePath) => {
    const canonical = traceAddressOf(full);
    const settled = { data: full, loading: false, hydrated: true, error: null, keptTraceId: null };
    setOpenTabs((prev) => {
      const mine = prev.find((t) => t.type === "trace" && t.id === address);
      if (canonical !== address && prev.some((t) => t.type === "trace" && t.id === canonical)) {
        // The trace is already open under its address: land there, drop the
        // provisional tab.
        return prev.filter((t) => t !== mine).map((t) => (t.type === "trace" && t.id === canonical ? { ...t, ...settled } : t));
      }
      if (mine) return prev.map((t) => (t === mine ? { ...t, id: canonical, ...settled } : t));
      return [...prev, makeTraceTab(canonical, full, originScopePath, { loading: false, hydrated: true })];
    });
    if (canonical !== address && endpointTabRef.current === `trace:${address}`) onEndpointTabChange?.(`trace:${canonical}`);
  }, [onEndpointTabChange]);

  const markTraceUnavailable = useCallback((address, attempt) =>
    query("getKeptCopy", { id: address }).catch(() => null).then((kept) => {
      setOpenTabs((prev) => prev.map((t) =>
        t.type === "trace" && t.id === address && t.attempt === attempt
          ? { ...t, loading: false, hydrated: false, keptTraceId: kept || null, error: kept ? null : ITEM_UNAVAILABLE_MESSAGE }
          : t
      ));
    }), []);

  useEffect(() => {
    if (!endpointTab?.startsWith("trace:")) return;
    const address = endpointTab.slice("trace:".length);
    if (!address) return;
    if (openTabsRef.current.some((t) => t.type === "trace" && t.id === address)) return;
    if (traceHydrationsRef.current.has(address)) return;
    traceHydrationsRef.current.add(address);
    // Scope tabs are URL-backed too, so a reload can still tell us that this
    // trace came from Signals. Keep that origin to re-arm the evidence jump.
    const originScopePath = [...scopePath];
    // A trace deep link means "show me this trace" — flip to context so the
    // trace isn't left loading hidden behind chat (mirrors handleSelectTrace).
    // Only for a fresh hydration: the early returns above already bailed if the
    // tab is open, so an interactive chat switch on a live tab is untouched.
    setPanelMode("context");
    const attempt = ++hydrationAttemptRef.current;
    setOpenTabs((prev) => {
      if (prev.some((t) => t.type === "trace" && t.id === address)) return prev;
      // A composed address names its session: the placeholder carries it, so
      // nothing takes the address for a request id while the trace loads.
      const { sessionId } = parseTraceAddress(address);
      return [...prev, makeTraceTab(address, { ...makeLoadingTrace(address), sessionId: sessionId || "" }, originScopePath, { loading: true, hydrated: false, attempt })];
    });
    query("getTrace", { id: address }).then((full) => {
      if (!full) return markTraceUnavailable(address, attempt);
      landTrace(address, full, originScopePath);
    }).catch((err) => {
      // getTrace is a RAW row-level read — an aggregate-only role gets a
      // 403. Without this catch the tab is stuck on makeLoadingTrace's
      // permanent fake "Loading trace". Stamp role-aware copy so the
      // Context panel renders an honest denial, not a frozen placeholder.
      const copy = analyticsErrorCopy(err, caps);
      setOpenTabs((prev) => prev.map((t) =>
        t.type === "trace" && t.id === address ? { ...t, loading: false, hydrated: false, error: copy.message } : t
      ));
    }).finally(() => {
      traceHydrationsRef.current.delete(address);
      setOpenTabs((prev) => prev.map((t) =>
        t.type === "trace" && t.id === address ? { ...t, loading: false } : t
      ));
    });
  }, [endpointTab, caps, scopePath, landTrace, markTraceUnavailable]);

  // A `session:<id>` tab key is the older address of the whole conversation;
  // it is the same trace tab, so the key becomes `trace:<id>` and the trace
  // deep link above hydrates it.
  useEffect(() => {
    if (endpointTab?.startsWith("session:")) onEndpointTabChange?.(`trace:${endpointTab.slice("session:".length)}`);
  }, [endpointTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Command deep-link hydration: a `command:<event_id>` tab from the URL
  // (reload / shared link) is ID-anchored, so unlike a pinned cell it can
  // be rehydrated without the originating scope. Seed a tab if the row
  // isn't in the current scoped list, then load its typed details by id.
  useEffect(() => {
    if (!endpointTab?.startsWith("command:")) return;
    const commandId = endpointTab.slice("command:".length);
    if (!commandId) return;
    const existing = openTabsRef.current.find((t) => t.type === "command" && t.id === commandId);
    if (existing?.hydrated || existing?.loading) return;
    if (commandHydrationsRef.current.has(commandId)) return;
    // Same as the trace deep link above — a command deep link should reveal
    // the context panel, not hydrate behind chat.
    setPanelMode("context");
    setOpenTabs((prev) => {
      if (prev.some((t) => t.type === "command" && t.id === commandId)) return prev;
      const seed = commandLookup.get(commandId) || makeLoadingCommand(commandId);
      return [...prev, { type: "command", id: commandId, data: seed, loading: true, hydrated: false }];
    });
    hydrateCommand(commandId);
  }, [endpointTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Active tab data — resolved from cache, independent of current scope
  const activeTab = openTabs.find((t) => `${t.type}:${t.id}` === endpointTab);
  // A trace tab shows a conversation: the whole of it when its id is a
  // session's, up to one request when its id is that request's (getTrace).
  const detailTab = activeTab?.type === "trace" ? activeTab : null;
  const selectedTrace = detailTab ? detailTab.data : null;
  const selectedTraceLoading = Boolean(detailTab?.loading);
  // Role-aware denial copy stamped by the getTrace .catch when a raw read
  // is refused (aggregate-only role). The request/payload tab renders this
  // instead of the permanent fake "Loading trace" placeholder.
  const keptTraceId = detailTab?.keptTraceId || null;
  const openKeptCopy = useCallback(() => {
    if (keptTraceId) onEndpointTabChange?.(`trace:${keptTraceId}`);
  }, [keptTraceId, onEndpointTabChange]);
  const keptCopyNotice = useMemo(
    () => (keptTraceId ? <KeptCopyNotice onOpen={openKeptCopy} /> : null),
    [keptTraceId, openKeptCopy],
  );
  const selectedTraceError = detailTab ? (keptCopyNotice ?? detailTab.error ?? null) : null;
  const selectedCommand = activeTab?.type === "command" ? activeTab.data : null;
  const selectedCommandLoading = activeTab?.type === "command" && activeTab.loading;
  const pinnedCell = activeTab?.type === "cell" ? activeTab.data : null;
  const lensTab = activeTab?.type === "lens" ? activeTab.data : null;
  // For trace log highlighting: only a row in the current scope, at either
  // grain (a request row's id is the request's, a session row's the session's).
  const selectedTraceId = selectedTrace && (traceLookup.has(selectedTrace.id) || sessions.some((row) => row.id === selectedTrace.id))
    ? selectedTrace.id : "";
  const selectedCommandId = selectedCommand && commandLookup.has(selectedCommand.id) ? selectedCommand.id : "";

  const scopeKey = encodeScopePath(deferredScopePath);

  // Right-column Context for a SIGNALS scope. The Contrast grid (endpoints ×
  // clusters) is meaningless on a signal page, so swap it for content that
  // matches the scope: the signal's own definition when a specific signal is in
  // scope, or a catalog of every defined signal on the all-signals lens
  // (focusDim === "signals"). Mirrors the lens resolver's precedence — a focused
  // signals dim wins over any terminal value step. A selected trace/command/
  // pinned cell still takes over the panel (handled by the branches below).
  const allSignalsFocused = focusDim === "signals";
  // A memory scope (focused memories dim, or any memories step in the path)
  // takes over the right column with a scope-aware memory panel instead of the
  // generic Contrast grid — same idea as the signal-scope branches below.
  // Gated on the dimension being visible so a hand-crafted deep link cannot
  // render a scope the current analytics mode does not expose.
  const memoryScoped = isDimensionVisible("memories")
    && (focusDim === "memories" || scopePath.some((s) => s.dim === "memories"));
  const memoryStepForTitle = [...scopePath].reverse().find((s) => s.dim === "memories");
  const memorySlugsForTitle = memoryStepForTitle ? (memoryStepForTitle.ids?.length ? memoryStepForTitle.ids : memoryStepForTitle.id != null ? [memoryStepForTitle.id] : []) : [];
  const memorySingle = memorySlugsForTitle.length === 1 && !isMemoryProjectId(memorySlugsForTitle[0]);
  const signalScopeStep = scopePath.length ? scopePath[scopePath.length - 1] : null;
  const signalCommand = (!allSignalsFocused && signalScopeStep?.dim === "signals")
    ? (signalScopeStep.ids?.[0] ?? signalScopeStep.id ?? null)
    : null;
  const signalTitle = signalCommand
    ? (findTriggerByCommand(null, signalCommand)?.title || `/${signalCommand}`)
    : null;
  // Signal content shows only when nothing more specific has claimed the right
  // panel — a selected trace/command, a pinned cell, or a lens detail all win.
  const specificPanelActive = !!(lensTab || pinnedCell || selectedTrace || selectedCommand);
  const showSignalDefinition = !specificPanelActive && !!signalCommand;
  const showSignalCatalog = !specificPanelActive && allSignalsFocused;

  // Right-column "context" mode is meaningful only when it has content. The
  // Contrast grid (the no-detail filler) is a gated comparison surface; with it
  // off, context is offered only when a specific detail is mounted.
  // hasContextDetail = everything that gives the right column real content
  // besides the Contrast filler.
  const comparisons = comparisonsEnabled();
  const hasContextDetail = !!(
    lensTab || pinnedCell || selectedTrace || selectedTraceError
    || selectedCommand || showSignalDefinition || showSignalCatalog || memoryScoped
  );
  const contextAvailable = contextModeAvailable({ comparisons, hasContextDetail });
  // A trace/command deep link (?tab=trace:… / command:…) hydrates its tab
  // asynchronously — on cold load / a shared link opened in a fresh browser,
  // openTabs is still empty for a render or two, so hasContextDetail is
  // transiently false. That startup gap is NOT an emptied detail; don't let
  // the empty-context reset below mistake it for one and knock the panel back
  // to chat, or the deep-linked trace loads hidden behind the chat pane.
  const deeplinkDetailPending =
    (endpointTab?.startsWith("trace:") || endpointTab?.startsWith("command:"))
    && !hasContextDetail;
  // A hand-over switches the panel before its tab is the page's (see
  // onActivate above); read as emptied, that gap knocked the panel back to the
  // chat and forward again, collapsing the column each way.
  const handoverPending = handoverTab != null && endpointTab !== handoverTab;
  // The scope-level "attribution" tab carries the scope's OWN context view —
  // the memory store, a signal definition / the all-signals catalog, or the
  // Contrast grid. Contrast is gated behind comparisons, but the memory/signal
  // overrides stand on their own, so the tab must appear for any of them (raw
  // scope/focus state, independent of whichever detail tab is active).
  const showScopeContextTab = comparisons || memoryScoped || allSignalsFocused || !!signalCommand;
  // When context goes empty (e.g. the last trace tab was closed) and there's no
  // Contrast filler, return the column to its resting view: chat, still open.
  // Only an explicit collapse hides the column. A chat-denied role has no chat
  // to rest on (steering there would ping-pong against the canChat steer
  // above), so its column collapses instead. A layout effect, so the empty
  // context never paints.
  useLayoutEffect(() => {
    if (panelMode !== "context" || contextAvailable || deeplinkDetailPending || handoverPending) return;
    if (canChat) setPanelMode("chat");
    else window.dispatchEvent(new CustomEvent("origin-detail-collapse"));
  }, [canChat, panelMode, contextAvailable, deeplinkDetailPending, handoverPending]);

  // Any scope change switches the command-log Context window off. The window
  // anchors to the focal trace's timestamp (windowAfterT/windowBeforeT), and the
  // focal trace tab is deliberately kept across scope pivots (App.jsx scope-
  // change effect), so a window from the previous scope would otherwise keep
  // clamping the new scope's command log to a ~60s slice its events can't fall
  // in — emptying it. Turning Context off on every scope change is the simple,
  // predictable rule (re-enable with the toggle, or it re-engages when you next
  // focus a trace). Skips the initial mount so Context keeps its default-on
  // behavior on first load.
  const prevScopeKeyRef = useRef(scopeKey);
  useEffect(() => {
    if (prevScopeKeyRef.current === scopeKey) return;
    prevScopeKeyRef.current = scopeKey;
    setCommandContextOn(false);
  }, [scopeKey]);

  // Focusing a new trace switches Context back on — the mirror of the scope
  // rule above, so the toggle is a consistent function of the last action:
  // change scope → widen (off), focus a trace → narrow (on). Only fires when
  // a (truthy) focal trace actually changes; closing the last trace leaves
  // Context as-is. Skips the initial mount so a deep-linked focal trace keeps
  // Context's default-on without a redundant re-set.
  const prevFocalRef = useRef(selectedTraceRequestId);
  useEffect(() => {
    if (prevFocalRef.current === selectedTraceRequestId) return;
    prevFocalRef.current = selectedTraceRequestId;
    if (selectedTraceRequestId) setCommandContextOn(true);
  }, [selectedTraceRequestId]);

  // Opens a trace: `traceId` is a request id or a session id, `seedRow` the
  // log row the click came from when the caller has one. The row names its
  // session, so a request row opens under `session@request` and a session row
  // under `session`, seeded with the row so the panel opens on it rather than
  // a placeholder; a bare id opens under itself and takes its address once
  // the trace lands (landTrace).
  function handleSelectTrace(traceId, seedRow = null) {
    // Opening anything into the context panel (from the focus column's
    // trace log) reveals it — flip the right column to Context so the
    // user isn't left looking at the chat while the trace loads behind it.
    setPanelMode("context");
    revealDetail();
    // Snapshot the scope the user opened this trace from so we can offer a
    // "return to origin" affordance if they drill into its anchors (same
    // pattern as pinned cells).
    const originScopePath = [...scopePath];
    const shallow = seedRow || traceLookup.get(traceId);
    const address = shallow ? traceAddressOfRow(shallow) : traceId;
    const tabKey = `trace:${address}`;
    const existingTab = openTabsRef.current.find((t) => t.type === "trace" && t.id === address);
    // The request a row click is about, beside the moment: sibling calls in a
    // session can share a millisecond, and the moment alone cannot tell them
    // apart. A tab already open at this moment for another of them is
    // re-opened on the one clicked.
    const clickedRequest = shallow && shallow.sessionId && shallow.sessionId !== shallow.id ? shallow.id : null;
    const sameRequest = !clickedRequest || focalRequestIdOf(existingTab?.data) === clickedRequest;

    if ((existingTab?.hydrated || existingTab?.loading) && sameRequest) {
      onEndpointTabChange?.(tabKey);
      return;
    }

    // Open the tab immediately with whatever we have so the panel feels
    // responsive; then hydrate with full detail (turns, tools, settings)
    // from the API.
    const attempt = ++hydrationAttemptRef.current;
    setOpenTabs((prev) => {
      const seed = shallow || existingTab?.data || makeLoadingTrace(traceId);
      if (prev.some((t) => t.type === "trace" && t.id === address)) {
        return prev.map((t) =>
          t.type === "trace" && t.id === address ? { ...t, data: seed, originScopePath, loading: true, hydrated: false, error: null, keptTraceId: null, attempt } : t
        );
      }
      return [...prev, { type: "trace", id: address, data: seed, originScopePath, loading: true, hydrated: false, attempt }];
    });
    onEndpointTabChange?.(tabKey);

    query("getTrace", { id: address, eventTime: shallow?.eventTime, ...(clickedRequest ? { requestId: clickedRequest } : {}) }).then((full) => {
      if (!full) return markTraceUnavailable(address, attempt);
      landTrace(address, full, originScopePath);
    }).catch((err) => {
      // Raw-read denial (aggregate-only role) — surface role-aware copy
      // instead of leaving the tab pinned on the fake "Loading trace"
      // seed forever.
      const copy = analyticsErrorCopy(err, caps);
      setOpenTabs((prev) => prev.map((t) =>
        t.type === "trace" && t.id === address ? { ...t, loading: false, hydrated: false, error: copy.message } : t
      ));
    }).finally(() => {
      setOpenTabs((prev) => prev.map((t) =>
        t.type === "trace" && t.id === address ? { ...t, loading: false } : t
      ));
    });
  }

  // ── The record axis's later pages ──
  //
  // A trace tab's axis is the conversation record, read newest-first in
  // pages: getTrace lands the newest page with the trace, and "earlier"
  // prepends the page before the oldest turn on the axis (getSessionPage). The
  // reads live in sessionTabReader over this page's tab list; the transitions
  // in sessionTabState. Created once: it keeps which reads are in flight.
  const [sessionReader] = useState(() => createSessionTabReader({
    query,
    tabs: () => openTabsRef.current,
    setTabs: setOpenTabs,
  }));
  // The detail panel's one control on the axis: read the page before the
  // oldest turn shown. Stable identity; it resolves the active tab at call time.
  const handleLoadEarlier = useCallback(() => {
    const tab = openTabsRef.current.find((t) => `${t.type}:${t.id}` === endpointTabRef.current);
    if (tab?.type === "trace") sessionReader.readPage(tab.id);
  }, [sessionReader]);

  // Load typed event details for a command by id and patch its
  // tab. Shared by the click handler and the deep-link effect; the ref
  // dedupes concurrent hydrations of the same event.
  function hydrateCommand(commandId) {
    if (commandHydrationsRef.current.has(commandId)) return;
    commandHydrationsRef.current.add(commandId);
    const known = commandLookup.get(commandId) || openTabsRef.current.find((tab) =>
      tab.type === "command" && tab.id === commandId && tab.data?.eventType)?.data;
    query("getEvent", { id: commandId, endpointId: known?.endpointId, timestamp: known?.timestamp }).then((full) => {
      if (!full) {
        setOpenTabs((prev) => prev.map((t) =>
          t.type === "command" && t.id === commandId
            ? { ...t, loading: false, hydrated: false, error: ITEM_UNAVAILABLE_MESSAGE }
            : t
        ));
        return;
      }
      setOpenTabs((prev) => prev.map((t) =>
        t.type === "command" && t.id === commandId
          ? { ...t, data: { ...t.data, ...full }, loading: false, hydrated: true, error: null }
          : t
      ));
    }).catch((err) => {
      const copy = analyticsErrorCopy(err, caps);
      setOpenTabs((prev) => prev.map((t) =>
        t.type === "command" && t.id === commandId
          ? { ...t, hydrated: false, error: copy.message }
          : t
      ));
    }).finally(() => {
      commandHydrationsRef.current.delete(commandId);
      setOpenTabs((prev) => prev.map((t) =>
        t.type === "command" && t.id === commandId ? { ...t, loading: false } : t
      ));
    });
  }

  function handleSelectCommand(commandId) {
    setPanelMode("context");
    revealDetail();
    const tabKey = `command:${commandId}`;
    const shallow = commandLookup.get(commandId);
    const existingTab = openTabsRef.current.find((t) => t.type === "command" && t.id === commandId);
    if (existingTab?.hydrated || existingTab?.loading) {
      onEndpointTabChange?.(tabKey);
      return;
    }
    // Open immediately with the shallow list row (synthesized one-liner)
    // so the panel feels responsive, then hydrate the full payload.
    setOpenTabs((prev) => {
      const seed = shallow || existingTab?.data || makeLoadingCommand(commandId);
      if (prev.some((t) => t.type === "command" && t.id === commandId)) {
        return prev.map((t) =>
          t.type === "command" && t.id === commandId ? { ...t, data: seed, loading: true, hydrated: false, error: null } : t
        );
      }
      return [...prev, { type: "command", id: commandId, data: seed, loading: true, hydrated: false }];
    });
    onEndpointTabChange?.(tabKey);
    hydrateCommand(commandId);
  }

  // Lens-opened detail (signal definitions, skill version bodies) joins
  // the strip as a first-class tab instead of taking over the panel body.
  // The old takeover left the trace tabs rendered above content they
  // didn't own — "View definition" looked like it hijacked whichever tab
  // was active. Keyed by detailKey so re-opening the same definition
  // refreshes the existing tab in place (save/fork rewrites) rather than
  // stacking duplicates.
  function handleOpenLensDetail(detail) {
    if (!detail) return;
    setPanelMode("context");
    revealDetail();
    const id = detail.detailKey || detail.title || "detail";
    const tabKey = `lens:${id}`;
    setOpenTabs((prev) => {
      const existing = prev.find((t) => t.type === "lens" && t.id === id);
      if (existing) return prev.map((t) => (t.type === "lens" && t.id === id ? { ...t, data: detail } : t));
      return [...prev, { type: "lens", id, data: detail }];
    });
    onEndpointTabChange?.(tabKey);
  }

  // The per-signal lens's "View definition" re-orients the right panel back
  // to its DEFAULT context tab (the "attribution" tab) instead of stacking a
  // duplicate definition lens tab. On a signal scope that tab already renders
  // SignalDefinitionPanel (showSignalDefinition below), so re-activating it
  // snaps you back to the definition when traces/commands are open.
  function handleShowSignalDefinition() {
    setPanelMode("context");
    onEndpointTabChange?.("attribution");
  }

  // Lens and pinned-cell tabs contain live, in-memory snapshots — they can't
  // be rehydrated from the URL after a reload the way trace/command tabs are.
  // Normalize either dangling local-only key back to attribution instead of
  // leaving the strip with no active tab.
  useEffect(() => {
    const [kind, ...idParts] = String(endpointTab || "").split(":");
    const id = idParts.join(":");
    if (!["lens", "cell"].includes(kind) || !id) return;
    if (!openTabsRef.current.some((t) => t.type === kind && t.id === id)) {
      onEndpointTabChange?.("attribution");
    }
  }, [endpointTab]); // eslint-disable-line react-hooks/exhaustive-deps

  function handlePinCell(cell) {
    if (!cell || !cell.cluster) return;
    setPanelMode("context");
    revealDetail();
    // Snapshot binds to the scope at pin time so the query stays stable even
    // if the user navigates elsewhere before revisiting the tab.
    const cellScopeKey = encodeScopePath(scopePath) || "overview";
    const id = `${cellScopeKey}::${cell.cluster.id}::${cell.bucketStart}-${cell.bucketEnd}`;
    const tabKey = `cell:${id}`;
    setOpenTabs((prev) => {
      if (prev.some((t) => t.type === "cell" && t.id === id)) return prev;
      return [...prev, { type: "cell", id, data: { cell, scopePath: [...scopePath] } }];
    });
    onEndpointTabChange?.(tabKey);
  }

  function handleCloseTab(tab) {
    const tabKey = `${tab.type}:${tab.id}`;
    setOpenTabs((prev) => prev.filter((t) => !(t.type === tab.type && t.id === tab.id)));
    if (endpointTab === tabKey) {
      // Switch to the nearest remaining tab, or attribution
      const remaining = openTabs.filter((t) => `${t.type}:${t.id}` !== tabKey);
      onEndpointTabChange?.(remaining.length ? `${remaining[remaining.length - 1].type}:${remaining[remaining.length - 1].id}` : "attribution");
    }
  }

  // Arm a brand-new influence-trace tab for a turn of the active trace.
  // Opening the tab is owned by App (explorer strip); the trace stays in
  // the right column (panel → context) so the surface and the trace sit
  // side by side. forceNew there means re-tracing never clobbers an open
  // tab. The view re-fetches the trace by id, so only ids are needed.
  function armInfluenceTrace(turnIndex) {
    if (!selectedTrace) return;
    // Influence tracing fans a multi-step analytics run across the LLM
    // proxy. A role without analytics access would 403 every step (wasted
    // proxy spend) — refuse to arm it. hasPermission fails open while caps
    // load, so a permitted user is never blocked mid-fetch.
    if (!canAnalytics) return;
    setPanelMode("context");
    onOpenInfluenceTab?.(selectedTrace.id, turnIndex);
  }

  // ── Scope identity ──
  const deepestStep = deferredScopePath[deferredScopePath.length - 1];
  const deepestDim = deepestStep ? DIMENSIONS[deepestStep.dim] : null;
  const scopeLabel = deepestStep ? scopeStepLabel(deepestStep) : "Organization";
  const scopeTitle = deepestStep ? scopeStepLabel(deepestStep) : "Overview";
  const showEndpoint = !deferredScopePath.some((s) => s.dim === "endpoints");

  // Real totals from overview analytics — the previous derivation summed over
  // the 1K-trace page, so "Requests" topped out at 1K and endpoint /
  // session counts only reflected whatever fit in that page.
  const { data: overviewMetrics } = useQuery("getOverviewMetrics", {
    scopePath: deferredScopePath,
    query: steeredQuery,
    range,
  });

  const hero = useMemo(() => {
    const dash = "\u2013";
    const have = overviewMetrics && typeof overviewMetrics.requests === "number";
    const metrics = have
      ? [
          { label: "Requests", value: formatCompact(overviewMetrics.requests) },
          { label: "Active endpoints", value: formatCompact(overviewMetrics.endpoints) },
          { label: "Sessions", value: formatCompact(overviewMetrics.sessions) },
        ]
      : [{ label: "Requests", value: dash }, { label: "Active endpoints", value: dash }, { label: "Sessions", value: dash }];

    const eyebrow = deferredScopePath.length === 0
      ? "Dashboard"
      : <ScopeBreadcrumb scopePath={deferredScopePath} onChange={onScopePathChange} />;

    return {
      eyebrow,
      title: scopeTitle,
      metrics,
      chartTitle: `${scopeTitle} activity`,
      controls: (
        <ScopeEscalationControl scopePath={deferredScopePath} labelFor={scopeStepLabel} />
      ),
    };
  }, [deepestDim, scopeTitle, overviewMetrics, deferredScopePath, onScopePathChange]);

  // ── Clusters (derived from filtered traces for the swimlane) ──
  const clusters = useMemo(() => {
    const counts = new Map();
    for (const t of traces) {
      for (const cid of t.clusterIds || []) {
        const c = counts.get(cid);
        if (c) c.sessions++;
        else counts.set(cid, { id: cid, name: scopeStepLabel({ dim: "clusters", id: cid }), sessions: 1 });
      }
    }
    return [...counts.values()].sort((a, b) => b.sessions - a.sessions);
  }, [traces]);

  const traceLog = {
    selectedTraceId,
    onSelectTrace: handleSelectTrace,
    showEndpoint,
    title: "Requests",
    searchPlaceholder: "Trace id, summary, endpoint, model, topic",
    // Show the loading affordance the instant a key is pressed (while the
    // debounce timer is still counting down), not only once the request fires.
    loading: tracesLoading || traceQueryPending,
    // Role-aware copy for a raw-analytics denial (null on success / non-403).
    // TraceLogPanel renders it in place of the empty state so the customer
    // never sees the raw gRPC status string (denialReason.js contract).
    denial: tracesDenial,
    sort: traceSort,
    onSortChange: setTraceSort,
    query: traceQuery,
    onQueryChange: onTraceQueryChange,
    suggestValues: querySuggestValues,
    onLoadMore: () => setTraceLimit((n) => n + PAGE),
  };
  const sessionLog = {
    loading: sessionsLoading || traceQueryPending,
    denial: sessionsDenial,
    sort: sessionSort,
    onSortChange: setSessionSort,
    onLoadMore: () => setSessionLimit((n) => n + PAGE),
  };

  const [requestedTabKind, ...requestedTabPayloadParts] = String(endpointTab || "").split(":");
  const requestedTabPayload = requestedTabPayloadParts.join(":");
  const hydratableTabPending = ["trace", "session", "command"].includes(requestedTabKind)
    && Boolean(requestedTabPayload);
  const localOnlyTabResetPending = ["lens", "cell"].includes(requestedTabKind)
    && Boolean(requestedTabPayload);
  const danglingItemTab = endpointTab && endpointTab !== "attribution" && !activeTab
    && !hydratableTabPending && !localOnlyTabResetPending;
  const unavailableLink = activeTab?.error === ITEM_UNAVAILABLE_MESSAGE
    || influenceDescriptor?.error === ITEM_UNAVAILABLE_MESSAGE
    || danglingItemTab;

  // ── Render ──
  if (unavailableLink) return <UnavailableLinkPage onOpenDashboard={openCurrentOrganizationHome} />;
  return (
    <ScopedView
      hero={hero}
      panelMode={panelMode}
      dockVisible={dockVisible}
      onDetailCollapsedChange={setDetailCollapsed}
      rail={({ collapsed, onToggleCollapsed }) => (
        // Right-edge mode rail — switches the right column's mode. Collapse
        // chrome lives on the tab strip + focus header, not here. SplitPage
        // injects collapse state so the rail can reopen-into-mode; we also
        // tell it when the panel is hidden behind a full-column expand so
        // it dims rather than claiming a mode is active off screen.
        <ModeRail
          mode={panelMode}
          onModeChange={(id) => {
            setPanelMode(id);
            // If the detail panel is hidden because another column is
            // expanded full (e.g. the activity surface took over),
            // collapse that expand so the chosen mode is actually
            // visible — otherwise the click silently switches a mode you
            // can't see.
            if (detailHiddenByExpand && expandedColumnId) {
              window.dispatchEvent(new CustomEvent("origin-column-expand", { detail: { columnId: expandedColumnId, mode: "collapse" } }));
            }
          }}
          collapsed={collapsed}
          onToggleCollapsed={onToggleCollapsed}
          panelHidden={detailHiddenByExpand}
          // Context appears only when there's a context panel to show; Chat
          // only for a role that can read the LLM provider descriptor. The
          // rules live in rightPanelMode.js; only the glyphs attach here.
          modes={rightPanelModes({ contextAvailable, canChat }).map((m) => ({ ...m, icon: MODE_ICONS[m.id] }))}
        />
      )}
      surfaceScopeOverride={surfaceScopeOverride}
      influenceDescriptor={influenceDescriptor}
      onOpenTrace={handleSelectTrace}
      onInfluenceScopeNavigate={(dim, id) => onOpenScopeTab?.([{ dim, id }])}
      traces={traces}
      clusters={clusters}
      selectedCluster={null}
      selectedTrace={selectedTrace}
      range={range}
      query={steeredQuery}
      onSteerScope={onSteerScope}
      activityLens={activityLens}
      focusDim={focusDim}
      view={view}
      logTab={effectiveLogTab}
      canTracePrompts={canTracePrompts}
      promptsWithheldHere={promptsWithheldHere}
      canRawCommands={canRawCommands}
      theme={theme}
      palette={palette}
      onRangeChange={onRangeChange}
      onActivityLensChange={onActivityLensChange}
      onLogTabChange={onLogTabChange}
      focusHeader={focusHeader}
      resetKey={scopeKey || "overview"}
      traceLog={traceLog}
      sessionLog={sessionLog}
      sessions={sessions}
      commands={commands}
      commandLog={{
        selectedId: selectedCommandId,
        onSelect: handleSelectCommand,
        showEndpoint,
        denial: commandsDenial,
        title: "Commands",
        searchPlaceholder: "Command, tool, session, endpoint",
        loading: commandsLoading || commandSearchPending,
        sort: commandSort,
        onSortChange: setCommandSort,
        searchQuery: commandSearch,
        onSearchChange: setCommandSearch,
        eventTypes: commandEventTypes,
        onEventTypesChange: setCommandEventTypes,
        maxRows: TELEMETRY_PAGE_LIMIT,
        onLoadMore: commandLimit < TELEMETRY_PAGE_LIMIT
          ? () => setCommandLimit((n) => Math.min(TELEMETRY_PAGE_LIMIT, n + COMMAND_PAGE))
          : undefined,
        contextOn: commandContextOn,
        onContextToggle: setCommandContextOn,
        contextWindowMs: commandContextWindowMs,
        onContextWindowChange: setCommandContextWindowMs,
        contextDirection: commandContextDirection,
        onContextDirectionChange: setCommandContextDirection,
        contextFocalOnly: commandContextFocalOnly,
        onContextFocalOnlyChange: setCommandContextFocalOnly,
        focalTraceId: selectedTraceRequestId,
        focalTraceTimestamp:
          traceLookup.get(selectedTraceRequestId)?.timestamp
          ?? (hydratedTrace?.eventTime ? parseServerTimestamp(hydratedTrace.eventTime) : 0),
        attributionByCommand: attribution.byCommand,
        traceLookup,
        onOpenTrace: (id) => id && handleSelectTrace(id),
      }}
      clusterTraceGroupBy={(t) => t.endpointName}
      scopePath={scopePath}
      onNavigatePath={onScopePathChange}
      rightHeader={
        <TabStrip
          className="pt-0"
          actions={panelMode === "chat" && chatBar ? (
            // Each control is its own boxed button with full side borders;
            // the tab strip's gap-[2px] separates them — matching the real
            // tabs (gaps + borders both sides) rather than fusing them into
            // one seam. These are the chat's mode-specific controls and
            // stay in the strip; collapse-to-hidden now lives on the
            // right-edge mode rail (SplitPage drops its appended ">").
            <>
              <button
                type="button"
                data-ph-capture-attribute-action={phAttr(ATTRIBUTE.ACTION, ACTION.NEW_CHAT)}
                onClick={chatBar.onNew}
                title="New chat"
                aria-label="New chat"
                className="inline-flex min-h-[38px] w-[34px] shrink-0 items-center justify-center border border-b-0 border-[color:var(--line)] bg-[color:var(--control-bg)] text-[14px] leading-none text-[color:var(--muted-soft)] transition-colors hover:text-[color:var(--ink)] cursor-pointer"
              >
                +
              </button>
              {/* PDF export now lives as a per-tab download icon on each
                  chat tab (see TabStrip onExport) — no wide rail button. */}
              {/* Expand the chat column to fill the content area (2-col,
                  hiding the scope column) and restore to 1-col. This is a
                  distinct axis from collapse-to-hidden, which lives on the
                  right-edge mode rail. */}
              <button
                type="button"
                onClick={() => expandChatColumn("toggle")}
                title={chatColExpanded ? "Restore chat to one column" : "Expand chat to two columns"}
                aria-label={chatColExpanded ? "Restore chat to one column" : "Expand chat to two columns"}
                aria-pressed={chatColExpanded}
                className="inline-flex min-h-[38px] w-[34px] shrink-0 items-center justify-center border border-b-0 border-[color:var(--line)] bg-[color:var(--control-bg)] p-0 text-[color:var(--muted-soft)] transition-colors hover:text-[color:var(--ink)] cursor-pointer [&_svg]:block [&_svg]:h-3 [&_svg]:w-3"
              >
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  {/* Same expand/restore glyph the context panel uses:
                      four-corner outward arrows to expand, an × to restore. */}
                  {chatColExpanded ? (
                    <><path d="M4 12l8-8" /><path d="M12 12L4 4" /></>
                  ) : (
                    <><path d="M10 2h4v4" /><path d="M6 14H2v-4" /><path d="M14 2l-4 4" /><path d="M2 14l4-4" /></>
                  )}
                </svg>
              </button>
            </>
          ) : undefined}
          onReorder={panelMode === "chat" ? (chatBar?.onReorder ?? null) : null}
          tabs={panelMode === "chat" ? (chatBar?.tabs ?? []) : [
            // The scope-level tab carries the scope's own context view (memory
            // store / signal panel / Contrast). Offer it whenever any of those
            // has content — see showScopeContextTab. With nothing behind it
            // (Contrast gated off, no memory/signal scope) it's dropped, and the
            // rail drops Context too, so the column falls to chat.
            ...(showScopeContextTab ? [{
              id: "attribution",
              label: scopeLabel,
              active: endpointTab === "attribution",
              dot: "bg-[color:var(--green)]",
              onSelect: () => onEndpointTabChange?.("attribution"),
            }] : []),
            ...openTabs.map((tab) => {
              const tabKey = `${tab.type}:${tab.id}`;
              if (tab.type === "trace") {
                // One conversation, whole or up to a request: busy while its
                // header or a record page reads.
                return {
                  id: tabKey,
                  label: tab.error ? "Item unavailable" : tab.keptTraceId ? "Duplicate copy" : (tab.data?.summary || tab.id),
                  active: endpointTab === tabKey,
                  dot: tab.loading || tab.data?.record?.loading ? "animate-pulse bg-[color:var(--green)]" : undefined,
                  onSelect: () => onEndpointTabChange?.(tabKey),
                  onClose: () => handleCloseTab(tab),
                };
              }
              if (tab.type === "cell") {
                return {
                  id: tabKey,
                  label: formatCellTabLabel(tab.data),
                  title: "Pinned cell",
                  active: endpointTab === tabKey,
                  dot: "bg-[color:var(--green)]",
                  onSelect: () => onEndpointTabChange?.(tabKey),
                  onClose: () => handleCloseTab(tab),
                };
              }
              if (tab.type === "lens") {
                return {
                  id: tabKey,
                  label: tab.data?.title || tab.id,
                  title: tab.data?.eyebrow || "Detail",
                  active: endpointTab === tabKey,
                  dot: "bg-[color:var(--green)]",
                  onSelect: () => onEndpointTabChange?.(tabKey),
                  onClose: () => handleCloseTab(tab),
                };
              }
              return {
                id: tabKey,
                label: tab.error ? "Item unavailable" : (tab.data?.tool || tab.id),
                active: endpointTab === tabKey,
                dot: tab.data ? (tab.data.exitCode === 0 ? "bg-emerald-400" : "bg-red-400") : undefined,
                onSelect: () => onEndpointTabChange?.(tabKey),
                onClose: () => handleCloseTab(tab),
              };
            }),
          ]}
        />
      }
      traceNav={(() => {
        if (!selectedTrace || !selectedTraceRequestId) return null;
        const idx = traces.findIndex((t) => t.id === selectedTraceRequestId);
        if (idx < 0) return null;
        const prev = idx > 0 ? traces[idx - 1] : null;
        const next = idx < traces.length - 1 ? traces[idx + 1] : null;
        return {
          hasPrev: !!prev,
          hasNext: !!next,
          onPrev: prev ? () => handleSelectTrace(prev.id) : undefined,
          onNext: next ? () => handleSelectTrace(next.id) : undefined,
          position: `${idx + 1} / ${traces.length}`,
        };
      })()}
      contextTitle={
        lensTab ? (lensTab.title || "Detail")
        : pinnedCell ? formatCellTabLabel(pinnedCell)
        : selectedTrace ? (selectedTraceLoading ? "Loading trace" : "Trace")
        : selectedCommand ? "Command"
        : showSignalDefinition ? signalTitle
        : showSignalCatalog ? "All signals"
        : (memoryScoped && !selectedTrace) ? (memorySingle ? "Fleet spread" : "Memories in scope")
        : "Contrast"
      }
      contextByline={traceHeaderByline({
        trace: selectedTrace?.placeholder && !selectedTraceLoading ? null : selectedTrace,
        // A lens or pinned cell owns the title otherwise.
        headerOwnedByTrace: !lensTab && !pinnedCell,
        scopePath,
        onScopePathChange,
      })}
      contextEyebrow={
        lensTab ? (lensTab.eyebrow || "Detail")
        : showSignalDefinition ? "Signal definition"
        : showSignalCatalog ? "Signals"
        : (memoryScoped && !selectedTrace) ? "Memory"
        : "Context"
      }
      onOpenLensDetail={handleOpenLensDetail}
      onShowSignalDefinition={handleShowSignalDefinition}
      onClickCell={handlePinCell}
      detailContent={() =>
        lensTab ? (
          lensTab.content ? (
            // Rich detail (e.g. the signal definition panel) brings its
            // own scroll container; mount it directly so nested
            // ScrollAreas don't fight over the wheel.
            <div className="h-full min-h-0 min-w-0 overflow-hidden px-[2px]">{lensTab.content}</div>
          ) : (
            <ScrollArea className="min-h-0 h-full" contentClassName="px-[2px] py-[8px]">
              <div className="whitespace-pre-wrap break-words font-mono text-[12px] leading-[1.55] text-[color:var(--ink)]">{lensTab.body}</div>
            </ScrollArea>
          )
        ) : pinnedCell ? (
          <CellCoOccurrencePanel
            cell={pinnedCell.cell}
            scopePath={pinnedCell.scopePath}
            activeScopePath={scopePath}
            eyebrow="Pinned cell"
            onNavigatePath={onScopePathChange}
            pinnedFrom={{
              label: pinnedCell.scopePath.length
                ? scopeStepLabel(pinnedCell.scopePath[pinnedCell.scopePath.length - 1])
                : "Overview",
              onNavigate: () => onScopePathChange(pinnedCell.scopePath),
              onUnpin: () => handleCloseTab(activeTab),
              isCurrent: encodeScopePath(pinnedCell.scopePath) === (scopeKey || ""),
            }}
          />
        ) : selectedCommand ? (
          <CommandDetail
            command={selectedCommand}
            loading={selectedCommandLoading}
            error={activeTab?.error}
            onScopeNavigate={(dim, id, extra) => onScopePathChange([...scopePath, { dim, id }], extra)}
          />
        ) : showSignalDefinition ? (
          // A signal scope: show its definition (read/edit beside the live
          // traffic), not the endpoints × clusters Contrast grid.
          <div className="h-full min-h-0 min-w-0 overflow-hidden px-[2px]">
            <SignalDefinitionPanel command={signalCommand} />
          </div>
        ) : showSignalCatalog ? (
          // The all-signals lens: a catalog of every defined signal, not Contrast.
          <SignalsCatalogPanel
            onPickSignal={(cmd) =>
              onScopePathChange([...scopePath, { dim: "signals", ids: [cmd], id: cmd }])
            }
          />
        ) : (memoryScoped && !selectedTrace) ? (
          // A memory scope: a scope-aware memory panel (fleet spread / overview),
          // not the endpoints × clusters Contrast grid. A selected trace still
          // wins (you clicked a prompt to inspect it).
          <div className="h-full min-h-0 min-w-0 overflow-hidden px-[2px]">
            <MemoryScopePanel scopePath={scopePath} range={range} onNavigatePath={onScopePathChange} />
          </div>
        ) : (!comparisons && !selectedTrace && !selectedTraceError) ? (
          // Contrast grid is gated off and there's no trace to show — context
          // mode has nothing to render, so the layout effect above flips the
          // column to chat. Render nothing here in the meantime.
          null
        ) : (
          <ContextPanel
            selectedTrace={selectedTrace}
            traceError={selectedTraceError}
            activeTab={(selectedTrace || selectedTraceError) ? "request" : "attribution"}
            loadingTrace={selectedTraceLoading}
            onTabChange={(v) => onEndpointTabChange?.(v)}
            onOpenTrace={handleSelectTrace}
            onLoadEarlier={handleLoadEarlier}
            onScopeNavigate={(dim, id, extra) =>
              onScopePathChange([...scopePath, { dim, id }], extra)
            }
            onScopeNavigatePath={(steps) => onScopePathChange(steps)}
            scopePath={scopePath}
            baseContextId={scopeKey || "overview"}
            hideHeader
            onTraceInfluence={canAnalytics ? armInfluenceTrace : null}
            pinnedFrom={selectedTrace && activeTab?.originScopePath ? {
              label: activeTab.originScopePath.length
                ? scopeStepLabel(activeTab.originScopePath[activeTab.originScopePath.length - 1])
                : "Overview",
              onNavigate: () => onScopePathChange(activeTab.originScopePath),
              isCurrent: encodeScopePath(activeTab.originScopePath) === (scopeKey || ""),
            } : null}
            // Auto-scroll to the first signal only when the trace was opened from a
            // signals drilldown — otherwise browsing session history shouldn't
            // bounce the reader to a (possibly false-positive) signal span.
            autoScrollSignals={(activeTab?.originScopePath || []).some((s) => s?.dim === "signals")}
          />
        )
      }
    />
  );
}
